import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { EmailFields } from "./components/EmailFields";
import { useI18n } from '@/hooks/useI18n';
import { Info, UserCheck, UserX, Loader2 } from "lucide-react";
import { DashboardItinerary } from "@/lib/models/dashboard";
import { CustomerInfo } from "./components/CustomerInfo";

interface SendQuoteStep2EmailProps {
    itinerary: DashboardItinerary;
    sendEmail: boolean;
    onSendEmailChange: (value: boolean) => void;
    emailSubject: string;
    customMessage: string;
    onEmailSubjectChange: (value: string) => void;
    onCustomMessageChange: (value: string) => void;
    subjectPlaceholder: string;
    userExists?: boolean;
    isCheckingUser?: boolean;
}

export const SendQuoteStep2Email = ({
    itinerary,
    sendEmail,
    onSendEmailChange,
    emailSubject,
    customMessage,
    onEmailSubjectChange,
    onCustomMessageChange,
    subjectPlaceholder,
    userExists,
    isCheckingUser,
}: SendQuoteStep2EmailProps) => {
    const { t } = useI18n();

    return (
        <div className="space-y-6">
            {/* Customer Info */}
            <CustomerInfo itinerary={itinerary} compact />

            {/* User registration status box */}
            {isCheckingUser ? (
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                    <span>{t('itinerary.checkingUserExists')}</span>
                </div>
            ) : userExists ? (
                <div className="flex items-start gap-3 px-4 py-3 rounded-lg border border-green-200 bg-green-50">
                    <UserCheck className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <p className="font-medium text-green-800">{t('itinerary.userExistsTitle')}</p>
                        <p className="text-green-700 mt-0.5">{t('itinerary.userExistsInfo')}</p>
                    </div>
                </div>
            ) : (
                <div className="flex items-start gap-3 px-4 py-3 rounded-lg border border-amber-200 bg-amber-50">
                    <UserX className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <p className="font-medium text-amber-800">{t('itinerary.userNotFoundTitle')}</p>
                        <p className="text-amber-700 mt-0.5">{t('itinerary.userNotFoundInfo')}</p>
                    </div>
                </div>
            )}

            {/* Send Email Checkbox */}
            <div className="flex items-start space-x-3 p-4 border rounded-lg bg-gray-50">
                <Checkbox
                    id="send-email"
                    checked={sendEmail}
                    onCheckedChange={onSendEmailChange}
                    className="mt-0.5"
                />
                <div className="flex-1">
                    <Label
                        htmlFor="send-email"
                        className="text-sm font-medium cursor-pointer"
                    >
                        {t('itinerary.sendEmailToClient') || 'Send email to client'}
                    </Label>
                    <p className="text-xs text-gray-500 mt-1 flex items-start gap-1">
                        <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        <span>
                            {t('itinerary.sendEmailDescription') || 'Uncheck if you want to send the quote manually'}
                        </span>
                    </p>
                </div>
            </div>

            {/* Email Fields - visible only when sendEmail is true */}
            {sendEmail && (
                <div className="space-y-4 animate-in fade-in duration-300">
                    <EmailFields
                        emailSubject={emailSubject}
                        customMessage={customMessage}
                        onEmailSubjectChange={onEmailSubjectChange}
                        onCustomMessageChange={onCustomMessageChange}
                        subjectPlaceholder={subjectPlaceholder}
                    />
                </div>
            )}

            {/* Manual sending info - visible when sendEmail is false */}
            {!sendEmail && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 animate-in fade-in duration-300">
                    <div className="flex items-start gap-2">
                        <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-900">
                            <p className="font-medium mb-1">
                                {t('itinerary.manualSendingTitle') || 'Manual sending'}
                            </p>
                            <p className="text-blue-700">
                                {t('itinerary.manualSendingInfo') || 'You will be able to copy the shared link or download the PDF to send it manually to the client.'}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
